import './pincode-input.scss';

import {
  ChangeEvent,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { formatTestId } from '../../data/utils/string.utils';
import { Spinner } from '../spinner/spinner';

type PincodeInputProps = PropsWithChildren<{
  testId?: string | null | undefined;
  maxLength?: number;
  loading?: boolean;
  error?: string | { error: string; [key: string]: string };
  onComplete?: (code: string) => void;
  onInput?: (code: string) => void;
}>;

const sleep = (ms: number) =>
  new Promise(resolve => {
    setTimeout(resolve, ms);
  });

export function PincodeInput({
  testId,
  maxLength = 6,
  error,
  onComplete,
  onInput,
  loading = false,
}: PincodeInputProps) {
  const intl = useIntl();

  const getTestId = useCallback(
    (testIdType: string | string[] | null = null) =>
      formatTestId('input-pincode', testId, testIdType),
    [testId]
  );

  const [code, setCode] = useState<string>('');
  const codeRef = useRef<HTMLInputElement | null>(null);

  const [ready, setReady] = useState<boolean>(true);
  const clear = useCallback(() => {}, []);

  const onHandleCode = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value);
  }, []);

  useEffect(() => {
    (async () => {
      onInput?.(code);

      if (code.length === maxLength && ready) {
        setReady(false);
        onComplete?.(code);
        await sleep(100);
        if (codeRef.current) {
          codeRef.current.value = '';
        }
        setCode('');
        setReady(true);
      }
    })();
  }, [clear, code, maxLength, onComplete, onInput, ready]);

  useEffect(() => {
    const timer = setInterval(() => {
      codeRef.current?.focus();
    }, 250);

    return () => {
      clearInterval(timer);
    };
  }, [codeRef]);

  return (
    <div
      data-test-id={getTestId()}
      className={`form-group form-group--pincode${error ? ' invalid' : ''}`}
    >
      <div className="input-group">
        <input
          data-1p-ignore
          data-bwignore
          data-lpignore="true"
          data-test-id={getTestId('input')}
          type="text"
          autoComplete="off"
          maxLength={maxLength}
          onChange={onHandleCode}
          ref={codeRef}
          disabled={loading}
        />

        {loading ? (
          <Spinner />
        ) : (
          <div className="code">
            {[...new Array(maxLength)]
              .map((_value, index) => index + 1)
              .map(key => (
                <span
                  key={key}
                  className={key <= code.length ? 'selected' : ''}
                />
              ))}
          </div>
        )}

        {error && (
          <div data-test-id={getTestId('error')} className="input-error">
            {intl.formatMessage(
              {
                id: typeof error === 'string' ? error : error.error,
              },
              typeof error === 'string' ? {} : error
            )}
          </div>
        )}
      </div>
    </div>
  );
}
